import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api";

const GroupsPage = () => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [error, setError] = useState("");

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => {
      const res = await api.get("contacts/show-groups");
      return res.data?.data || [];
    }
  });

  const createGroup = useMutation({
    mutationFn: (payload) => api.post("contacts/add-group", payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["groups"]);
      setOpen(false);
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to create group");
    }
  });

  const updateGroup = useMutation({
    mutationFn: ({ id, payload }) =>
      api.put(`contacts/update-group/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["groups"]);
      setOpen(false);
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to update group");
    }
  });

  const deleteGroup = useMutation({
    mutationFn: (id) => api.delete(`contacts/delete-group/${id}`),
    onSuccess: () => queryClient.invalidateQueries(["groups"])
  });

  const openCreateModal = () => {
    setEditingGroup(null);
    setGroupName("");
    setError("");
    setOpen(true);
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setError("");
    setOpen(true);
  };

  const handleSubmit = () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      setError("Group name is required");
      return;
    }

    // Client-side duplicate check
    const isDuplicate = groups.some(g =>
      g.name.toLowerCase() === trimmedName.toLowerCase() &&
      (!editingGroup || g._id !== editingGroup._id)
    );

    if (isDuplicate) {
      setError("Group name already exists");
      return;
    }

    setError(""); // Clear previous errors

    if (editingGroup) {
      updateGroup.mutate({
        id: editingGroup._id,
        payload: { name: trimmedName }
      });
    } else {
      createGroup.mutate({ name: trimmedName });
    }
  };

  if (isLoading) {
    return <div style={{ padding: "20px", color: "#555" }}>Loading groups...</div>;
  }

  return (
    <div style={{ padding: "20px", backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
        <h1 style={{ fontSize: "28px", fontWeight: "600", color: "#333" }}>Groups</h1>
        <button
          onClick={openCreateModal}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            backgroundColor: "#9DC33B",
            color: "white",
            fontWeight: "500",
            cursor: "pointer",
            border: "none"
          }}
        >
          + Add Group
        </button>
      </div>

      {/* Table Card */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          border: "1px solid #ddd",
          overflow: "hidden"
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Arial, sans-serif"
          }}
        >
          <thead style={{ backgroundColor: "#f5f5f5", color: "#333" }}>
            <tr>
              <th style={{ textAlign: "left", padding: "12px", fontWeight: "600" }}>Group Name</th>
              <th style={{ textAlign: "left", padding: "12px", fontWeight: "600" }}>Total Contacts</th>
              <th style={{ textAlign: "left", padding: "12px", fontWeight: "600" }}>Created At</th>
              <th style={{ textAlign: "right", padding: "12px", fontWeight: "600" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: "20px", color: "#777" }}>
                  No groups found
                </td>
              </tr>
            )}

            {groups.map((group) => (
              <tr
                key={group._id}
                style={{ borderTop: "1px solid #eee", transition: "background 0.2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fafafa")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
              >
                <td style={{ padding: "12px", fontWeight: "500", color: "#222" }}>
                  {group.name}
                </td>
                <td style={{ padding: "12px", color: "#555" }}>
                  {group.contactCount ?? 0}
                </td>
                <td style={{ padding: "12px", color: "#555" }}>
                  {new Date(group.createdAt).toLocaleDateString()}
                </td>
                <td style={{ padding: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button
                      onClick={() => openEditModal(group)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        backgroundColor: "white",
                        cursor: "pointer",
                        color: "#333"
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteGroup.mutate(group._id)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "6px",
                        backgroundColor: "#e53e3e",
                        color: "white",
                        border: "none",
                        cursor: "pointer"
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "20px",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
            }}
          >
            <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
              {editingGroup ? "Edit Group" : "Create Group"}
            </h2>

            {error && (
              <div style={{ color: "#e53e3e", fontSize: "14px", marginBottom: "10px" }}>
                {error}
              </div>
            )}

            <input
              type="text"
              value={groupName}
              onChange={(e) => {
                setGroupName(e.target.value);
                if (error) setError("");
              }}
              placeholder="Enter group name"
              style={{
                width: "95%",
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "10px",
                marginBottom: "16px",
                outline: "none"
              }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => setOpen(false)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  backgroundColor: "white",
                  cursor: "pointer",
                  color: "#333"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  backgroundColor: "#9DC33B",
                  color: "white",
                  fontWeight: "500",
                  border: "none",
                  cursor: "pointer"
                }}
              >
                {editingGroup ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsPage;